import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { setFocus } from '@noriginmedia/norigin-spatial-navigation';
import NpvrSlider, { NpvrSliderItem } from './npvr-slider';
import { mapNpvrEventToCardProps } from '@smart-tv/utils/kmm/helpers';
import { useToast, IconTypes } from '@smart-tv/hooks/use-toast/use-toast';
import { deleteRecording } from '@smart-tv/utils/kmm/deleteRecording';
import { rootStore } from './_stores/store-root';
import { NpvrResourceKey } from './_stores/store-npvr';
import { NpvrEventItem } from '@smart-tv/typings/npvr';
import useDialog from '@smart-tv/hooks/use-dialog';
import { getNPVRActionFocusKey, getNPVRTabFocusKey, NpvrTab, NpvrTabProps } from './npvr-helpers';
import useDelayedSpinner from '@smart-tv/hooks/use-delayed-spinner/use-delayed-spinner';
import NpvrLoading from './npvr-loading';

export const NpvrPlanned: React.FC<NpvrTabProps> = observer(({ setDialogProps }) => {
  const { data: plannedNpvrEvents } = rootStore.npvrStore.scheduled;
  const pendingFocusKeyRef = React.useRef<string>();
  const { showToast } = useToast();
  const { handleSetDialogFocusKey } = useDialog();
  const showSpinner = useDelayedSpinner(plannedNpvrEvents === undefined);

  const assetPressHandler = () =>
    showToast('Programmet kan først afspilles, når det er optaget ', IconTypes.info);

  const handleButtonAction = (event: NpvrEventItem) => {
    event.npvrInfo?.isSeriesRecording ? handleOpenDialog(event) : handleCancel(false, event, false);
  };

  const handleCancel = async (
    seriesRecording: boolean,
    event: NpvrEventItem,
    fromDialog: boolean
  ) => {
    const seriesId = seriesRecording && event.metadataSerieId ? event.metadataSerieId : undefined;
    const cancelSeries = seriesId !== undefined;

    try {
      const cancelled = cancelSeries
        ? await deleteRecording(undefined, undefined, seriesId, undefined, true, true, false)
        : await deleteRecording(
            event.sid,
            event.eventId,
            undefined,
            undefined,
            false,
            false,
            false
          );

      if (cancelled) {
        const current = rootStore.npvrStore.scheduled.data ?? [];
        const indexOfCancelled = current.findIndex((item) => item.eventId === event.eventId);
        const remainingCount = cancelSeries
          ? current.filter((item) => item.metadataSerieId !== seriesId).length
          : current.length - 1;
        const nextFocusKey =
          remainingCount > 0
            ? getNPVRActionFocusKey(Math.max(0, Math.min(indexOfCancelled, remainingCount - 1)))
            : getNPVRTabFocusKey(NpvrTab.Storage);

        if (fromDialog) {
          handleSetDialogFocusKey(nextFocusKey);
        } else {
          pendingFocusKeyRef.current = nextFocusKey;
        }

        rootStore.npvrStore.removeScheduled(
          cancelSeries
            ? (item) => item.metadataSerieId === seriesId
            : (item) => item.eventId === event.eventId
        );
        rootStore.npvrStore.invalidate([NpvrResourceKey.Status]);

        showToast(
          cancelSeries ? 'Serieoptagelsen blev annulleret' : 'Optagelsen blev annulleret',
          IconTypes.success
        );
      } else {
        showToast('Der opstod en fejl under annullering af optagelsen', IconTypes.failure);
      }
    } catch (error) {
      console.error(error);
      showToast('Der opstod en fejl under annullering af optagelsen', IconTypes.failure);
    } finally {
      if (fromDialog) setDialogProps(null);
    }
  };

  const handleOpenDialog = (event: NpvrEventItem) =>
    setDialogProps({
      titleText: 'Hvad vil du annullere?',
      primaryButtonText: 'Annuller optagelse af episode',
      primaryButtonFunction: () => handleCancel(false, event, true),
      secondaryButtonText: 'Annuller serieoptagelse',
      secondaryButtonFunction: () => handleCancel(true, event, true)
    });

  useEffect(() => {
    if (pendingFocusKeyRef.current === undefined) return;

    setFocus(pendingFocusKeyRef.current);
    pendingFocusKeyRef.current = undefined;
  }, [plannedNpvrEvents]);

  useEffect(() => {
    rootStore.npvrStore.fetchScheduled().catch((error) => console.error(error));
  }, []);

  if (showSpinner) return <NpvrLoading />;
  if (plannedNpvrEvents === undefined) return null;

  const sliderItems: NpvrSliderItem[] = plannedNpvrEvents.map((event) => ({
    ...mapNpvrEventToCardProps(event, false),
    action: {
      label: 'Annuller',
      onClick: () => handleButtonAction(event)
    }
  }));

  const displayString =
    plannedNpvrEvents.length === 0
      ? 'Du har ingen planlagte optagelser.'
      : sliderItems.some((event) => event.errorMessageBadge)
      ? 'Der er ikke plads til at optage alle de optagelser, du har planlagt. Annuller eller slet optagelser for at frigiv plads.'
      : '';

  return (
    <>
      <NpvrSlider
        items={sliderItems}
        displayString={displayString}
        assetPressHandler={assetPressHandler}
      />
    </>
  );
});

export default NpvrPlanned;
